'use client';

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { authService } from '@/lib/authService';
import { buildApiUrl, isAppShellRoute } from '@/lib/platformUtils';

/**
 * A mailbox belonging to the user at a specific client.
 */
export interface AccessibleEmailMailbox {
  mailbox_id: number;
  email_address: string;
  display_name: string | null;
  is_primary: boolean;
  mailcow_status: string;
  is_virtual: boolean;
  domain_name: string | null;
}

/**
 * A client where the authenticated user has at least one active/virtual
 * email mailbox. Returned by GET /api/v1/email/accessible-clients.
 */
export interface AccessibleEmailClient {
  client_id: number;
  client_name: string;
  is_active_client: boolean;
  mailboxes: AccessibleEmailMailbox[];
}

/**
 * Hook that fetches all clients where the authenticated user has an active
 * or virtual email mailbox, and exposes a convenience `hasAnyMailbox` flag.
 *
 * This decouples the email module (and TotalConnectAccessGate) from the JWT
 * active client so a user with mailboxes across multiple clients can access
 * all of them from whichever client is currently active — Outlook-style.
 *
 * Unlike useAccessiblePabxClients, this hook does NOT manage a "selected
 * client" because the email module already switches between individual
 * mailboxes via activeSharedMailboxId / accountId. The client grouping is
 * used purely for sidebar presentation.
 */
export function useAccessibleEmailClients() {
  const [clients, setClients] = useState<AccessibleEmailClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  const fetchClients = useCallback(async (force = false) => {
    if (!force && fetchedRef.current) return;
    try {
      setLoading(true);
      setError(null);
      // Mailbox data is app-shell only — skip on public pages.
      if (typeof window !== 'undefined' && !isAppShellRoute(window.location.pathname)) {
        setLoading(false);
        return;
      }
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        setLoading(false);
        return;
      }

      const res = await fetch(buildApiUrl('/api/v1/email/accessible-clients'), {
        headers: { Authorization: `Bearer ${tokens.access_token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch accessible email clients');
      const data = await res.json();
      if (data.success && Array.isArray(data.clients)) {
        setClients(data.clients);
      } else {
        setClients([]);
      }
    } catch (err: unknown) {
      console.error('[useAccessibleEmailClients] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load accessible email clients');
    } finally {
      setLoading(false);
      fetchedRef.current = true;
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const hasAnyMailbox = useMemo(
    () => clients.some((c) => c.mailboxes.length > 0),
    [clients],
  );

  const hasMultipleClients = clients.length > 1;

  // All mailboxes flattened across clients (for the sidebar mailbox list)
  const allMailboxes = useMemo(
    () =>
      clients.flatMap((c) =>
        c.mailboxes.map((mb) => ({
          ...mb,
          client_id: c.client_id,
          client_name: c.client_name,
          is_active_client: c.is_active_client,
        })),
      ),
    [clients],
  );

  return {
    clients,
    loading,
    error,
    hasAnyMailbox,
    hasMultipleClients,
    allMailboxes,
    refresh: () => fetchClients(true),
  };
}

export default useAccessibleEmailClients;
